import { motion } from 'motion/react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AimdocLogo } from '../brand/AimdocLogo';
import type { StepKey } from './types';

export const STAGE_META: Record<StepKey, { label: string }> = {
  intent: { label: 'Goals' },
  connect: { label: 'Connect' },
  surfaces: { label: 'Surfaces' },
  website: { label: 'Website' },
  payments: { label: 'Payments' },
  building: { label: 'Build' },
};

const SPRING = { type: 'spring', stiffness: 260, damping: 30 } as const;

function StageNode({ state, number }: { state: 'done' | 'current' | 'todo'; number: number }) {
  return (
    <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
      {state === 'current' && (
        <motion.span
          layoutId="onboarding-stage-halo"
          className="absolute -inset-1 rounded-full bg-[#1173F0]/12 ring-1 ring-[#1173F0]/25 dark:bg-[#3B8BFF]/15"
          transition={SPRING}
        />
      )}
      <span
        className={cn(
          'relative flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold tabular-nums transition-colors duration-300',
          state === 'done' && 'border-[#1173F0] bg-[#1173F0] text-white',
          state === 'current' && 'border-[#1173F0] bg-background text-[#1173F0] dark:border-[#3B8BFF] dark:text-[#6AA8FF]',
          state === 'todo' && 'border-border bg-background text-muted-foreground'
        )}
      >
        {state === 'done' ? (
          <motion.span
            className="flex"
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 600, damping: 28 }}
          >
            <Check className="h-3 w-3" strokeWidth={3.25} />
          </motion.span>
        ) : (
          number
        )}
      </span>
    </span>
  );
}

/**
 * The stage map, dead-center in the top bar. Connectors fill with a spring
 * as the flow advances and the halo glides to the current stage. Labels for
 * completed and upcoming stages wait for lg; at md only the current stage is
 * labeled, so the bar never crowds.
 */
function StageSteps({ steps, current }: { steps: StepKey[]; current: StepKey }) {
  const index = Math.max(0, steps.indexOf(current));
  return (
    <ol className="flex items-center">
      {steps.map((key, i) => {
        const state = i < index ? 'done' : i === index ? 'current' : 'todo';
        return (
          <li key={key} className="flex items-center">
            {i > 0 && (
              <span className="relative mx-2.5 h-px w-6 overflow-hidden rounded-full bg-border lg:w-9" aria-hidden>
                <motion.span
                  className="absolute inset-0 origin-left bg-[#1173F0]"
                  initial={false}
                  animate={{ scaleX: i <= index ? 1 : 0 }}
                  transition={SPRING}
                />
              </span>
            )}
            <span className="flex items-center gap-2">
              <StageNode state={state} number={i + 1} />
              <span
                className={cn(
                  'text-xs font-medium transition-colors',
                  state === 'current' ? 'hidden text-foreground md:inline' : 'hidden text-muted-foreground lg:inline'
                )}
              >
                {STAGE_META[key].label}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The top chrome of the onboarding stage: logo left, the stage map centered,
 * the count right — so the content column below can sit dead-center in the
 * viewport. A hairline along the bottom edge fills with overall progress.
 * Below md the stage map collapses to the current label plus one segment per
 * stage that fills as you advance.
 */
export function StageBar({ steps, current }: { steps: StepKey[]; current: StepKey }) {
  const index = Math.max(0, steps.indexOf(current));
  const progress = steps.length > 1 ? index / (steps.length - 1) : 1;
  return (
    <header className="relative z-10 shrink-0 border-b border-border/60 bg-background/75 backdrop-blur-md">
      <div className="relative flex h-14 items-center px-5 sm:px-8">
        <AimdocLogo className="h-[26px] text-foreground" />
        <div className="absolute left-1/2 hidden -translate-x-1/2 md:block">
          <StageSteps steps={steps} current={current} />
        </div>
        <span className="ml-auto font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground md:hidden">
          {STAGE_META[current].label} · Step {index + 1} of {steps.length}
        </span>
        <span className="ml-auto hidden font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground md:block">
          Step {index + 1} of {steps.length}
        </span>
      </div>
      <div className="flex gap-1.5 px-5 pb-3 md:hidden" aria-hidden>
        {steps.map((key, i) => (
          <span key={key} className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
            <motion.span
              className="block h-full w-full origin-left rounded-full bg-[#1173F0]"
              initial={false}
              animate={{ scaleX: i <= index ? 1 : 0 }}
              transition={SPRING}
            />
          </span>
        ))}
      </div>
      <motion.span
        aria-hidden
        className="absolute inset-x-0 -bottom-px hidden h-px origin-left bg-gradient-to-r from-[#1173F0]/0 via-[#1173F0]/70 to-[#1173F0] md:block"
        initial={false}
        animate={{ scaleX: progress }}
        transition={{ type: 'spring', stiffness: 120, damping: 26 }}
      />
    </header>
  );
}
